import { NetworkIntegrityAggregate, NetworkIntegrityAggregateTableRow } from "../models";
import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { AttestationBasedAlertsDescription, NetworkIntegrityAggregateTable, NetworkIntegrityWidgetInfoFlyout, WidgetIcon, WidgetMenu, WidgetTitleBackgroundColor } from ".";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityActionStatus } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { NetworkIntegrityAggregateGroup, NetworkIntegrityJobStatus } from "@app/projects/enumerators";
import { Link } from "react-router-dom";
import { getPendoClassNameForAggregateGroup, getPendoClassNameForAggregateKey } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityPendoHelper";
import { WidgetLoading, WidgetHeader, Widget, WidgetBody, WidgetFooter } from "./Widget";
import Features from "@app/feature-flags/features";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { attestationBasedAlertDisplayKeyToAggregateKey } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityDisplayHelper";

const AttestationBasedAlertsWidget = (): JSX.Element => {
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const project = networkIntegrityContext.project!;
  const ffContext = useContext(FeatureFlagContext);
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const allowProviderDetailView = project.showNetworkIntegrityAttestationBasedAlerts ?? false;

  const [attestationBasedAlertResults, setAttestationBasedAlertResults] = useState<NetworkIntegrityAggregate[]>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const title = 'Attestation-based Alerts';
  const titleIcon = WidgetIcon.Critical;
  const titleBackgroundColor = WidgetTitleBackgroundColor.Red;

  useEffect(() => {
      let cancelled = false;
      setAttestationBasedAlertResults(null);
      async function fetchAttestationBasedAlertResults() {
          try {
            if (networkIntegrityContext.projectRunConfigurationId) {
              const result = await networkIntegrityService.getAttestationBasedAlertResults(project.projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes);
              if (!cancelled) setAttestationBasedAlertResults(result);
            }
          } finally {
              if (!cancelled) setLoading(false);
          };
      };

      if (networkIntegrityContext.isJobStatusLoading === false && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished) {
        setLoading(true);
        fetchAttestationBasedAlertResults();
      }

      return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, networkIntegrityContext.isJobStatusLoading]);

  const generateProviderCellContent = (aggregateKey: string, displayKey: string, afterTermedCount: boolean = false): JSX.Element => {
      const count = afterTermedCount ? attestationBasedAlertResults.find(a => a.aggregateKey === aggregateKey)?.afterTermedCount : attestationBasedAlertResults.find(a => a.aggregateKey === aggregateKey)?.count;
      if (!count) return <span>0</span>;

      return (allowProviderDetailView ? <Link className={`${getPendoClassNameForAggregateKey(attestationBasedAlertDisplayKeyToAggregateKey.get(displayKey))}`}
        to={{
          pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/providers/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.AttestationBasedAlerts}/${encodeURIComponent(aggregateKey)}`,
          state: {
            initialActionStatus: afterTermedCount ? NetworkIntegrityActionStatus.NoActionsApplied : undefined,
            initialRegionSetIds: networkIntegrityContext.regionSetIds,
            initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
            runConfiguration: networkIntegrityContext.projectRunConfiguration
          }
        }}>
        {count.toLocaleString()}
      </Link>
      : <span>{count.toLocaleString()}</span>
      );
  };

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading><WidgetHeader title={title} titleBackgroundColor={titleBackgroundColor} titleIcon={titleIcon} /></WidgetLoading>);

  const rows : NetworkIntegrityAggregateTableRow[] = [...attestationBasedAlertDisplayKeyToAggregateKey.keys()].map((key) => {
      return {aggregateKey: key}
  });

  if(attestationBasedAlertResults)
      rows.forEach((row) => {
        const niAggKey = attestationBasedAlertDisplayKeyToAggregateKey.get(row.aggregateKey);
        const resultEntry = attestationBasedAlertResults.find(a => a.aggregateKey === niAggKey);
        row.afterTermedCount = generateProviderCellContent(niAggKey, row.aggregateKey, true);
        row.count = generateProviderCellContent(niAggKey, row.aggregateKey);
        row.rawAfterTermedCount = resultEntry?.afterTermedCount ?? undefined;
        row.rawCount = resultEntry?.count ?? undefined;
      });

  const footer =
    ffContext.enabledFeatureFlags.has(Features.DISPLAY_NETWORK_INTEGRITY_TOTAL_AFTER_TERMED) && 
    allowProviderDetailView &&
    <WidgetFooter>
      <Link className={getPendoClassNameForAggregateGroup(NetworkIntegrityAggregateGroup.AttestationBasedAlerts, "view-all")}
        to={{
          pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/all/${NetworkIntegrityAggregateGroup.AttestationBasedAlerts}`,
          state: {
            initialProviderType: networkIntegrityContext.providerType,
            initialRegionSetIds: networkIntegrityContext.regionSetIds,
            initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
            runConfiguration: networkIntegrityContext.projectRunConfiguration
          }
        }}>
          View All
      </Link>
    </WidgetFooter>;

  return (
    <Widget>
      <WidgetHeader title={title} titleBackgroundColor={titleBackgroundColor} titleIcon={titleIcon}>
        <WidgetMenu>
          <NetworkIntegrityWidgetInfoFlyout title="Attestation-based Alerts" description={<AttestationBasedAlertsDescription/>} showLicenseInfo={!allowProviderDetailView} />
          {attestationBasedAlertResults && allowProviderDetailView && <Link
            className="qes-button qes-button-secondary"
            id="network-integrity-providers__download-report-link"
            to={{
              pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/reports/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.AttestationBasedAlerts}`,
              state: {
                reportRowCount: attestationBasedAlertResults.reduce((sum, current) => sum + current.count, 0),
                runConfiguration: networkIntegrityContext.projectRunConfiguration
              }
            }}
            type="button" >
            Create All Report
          </Link>}
        </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
        <NetworkIntegrityAggregateTable aggregateHeader="Alert" aggregateCountHeader="Unique Access Points" rows={rows} />
      </WidgetBody>
      {footer}
    </Widget>);
}

export { AttestationBasedAlertsWidget };